import React, { useState } from 'react';
import {
  Alert,
  Button,
  Modal,
  Progress,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd';
import {
  ProductCategory,
  useCreateProduct,
  useProducts,
  useUpdateProduct,
} from '@shared/api/hooks/useProducts';

interface ParsedRow {
  rowIndex: number;
  sku: string;
  nameIt: string;
  descIt: string;
  price: number;
  category: ProductCategory;
  images: string[];
  isAvailable: boolean;
  sizes: Array<{ size: string; stock: number }>;
  errors: string[];
}

interface Props {
  open: boolean;
  onClose: () => void;
}

const REQUIRED_HEADERS = ['sku', 'nameIt', 'descIt', 'price', 'category', 'sizes'];
const VALID_CATEGORIES: ProductCategory[] = ['JERSEY', 'SCARF', 'HAT', 'ACCESSORY', 'OTHER'];

function parseCsv(text: string): { rows: ParsedRow[]; headerError?: string } {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length === 0) return { rows: [], headerError: 'File vuoto' };

  const header = lines[0].split(',').map((s) => s.trim());
  const missing = REQUIRED_HEADERS.filter((h) => !header.includes(h));
  if (missing.length > 0) {
    return { rows: [], headerError: `Colonne mancanti: ${missing.join(', ')}` };
  }

  const idx = (col: string): number => header.indexOf(col);

  const rows: ParsedRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = splitCsvLine(lines[i]);
    const errors: string[] = [];

    const sku = cols[idx('sku')] ?? '';
    if (!sku) errors.push('sku obbligatorio');

    const nameIt = cols[idx('nameIt')] ?? '';
    if (!nameIt) errors.push('nameIt obbligatorio');

    const descIt = cols[idx('descIt')] ?? '';
    if (!descIt) errors.push('descIt obbligatorio');

    const priceRaw = cols[idx('price')] ?? '';
    const price = Number(priceRaw.replace(',', '.'));
    if (!priceRaw || Number.isNaN(price) || price < 0) {
      errors.push('price non valido');
    }

    const categoryRaw = (cols[idx('category')] ?? '').toUpperCase() as ProductCategory;
    if (!VALID_CATEGORIES.includes(categoryRaw)) {
      errors.push(`category non valida (${categoryRaw || 'vuoto'})`);
    }

    const imagesIdx = header.indexOf('images');
    const images =
      imagesIdx >= 0 && cols[imagesIdx]
        ? cols[imagesIdx].split('|').map((s) => s.trim()).filter(Boolean)
        : [];

    const availIdx = header.indexOf('isAvailable');
    const isAvailable = availIdx >= 0 ? cols[availIdx] !== 'false' : true;

    const sizesRaw = cols[idx('sizes')] ?? '';
    const sizes = sizesRaw
      .split('|')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((token) => {
        const [size, stockStr] = token.split(':');
        return {
          size: (size ?? '').trim().toUpperCase(),
          stock: Number(stockStr ?? '0'),
        };
      });
    if (sizes.length === 0) errors.push('almeno una size richiesta (es. S:10|M:5)');
    if (sizes.some((s) => !s.size || Number.isNaN(s.stock) || s.stock < 0)) {
      errors.push('formato sizes invalido (size:stock|size:stock)');
    }

    rows.push({
      rowIndex: i + 1,
      sku,
      nameIt,
      descIt,
      price,
      category: categoryRaw,
      images,
      isAvailable,
      sizes,
      errors,
    });
  }
  return { rows };
}

function splitCsvLine(line: string): string[] {
  // Minimal CSV split with " quoted fields. Sufficient for product imports.
  const out: string[] = [];
  let buf = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"' && line[i + 1] === '"') {
      buf += '"';
      i += 1;
    } else if (ch === '"') {
      inQuotes = !inQuotes;
    } else if (ch === ',' && !inQuotes) {
      out.push(buf);
      buf = '';
    } else {
      buf += ch;
    }
  }
  out.push(buf);
  return out.map((s) => s.trim());
}

export function CsvImportModal({ open, onClose }: Props): React.ReactElement {
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [headerError, setHeaderError] = useState<string | undefined>();
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const createMutation = useCreateProduct();
  const updateMutation = useUpdateProduct();
  // Limit 100 = backend max; ok per la maggior parte dei casi v2.
  // Se cresce, paginare lookup esistenti.
  const { data: existingPage } = useProducts({ limit: 100 });
  const existingBySku = new Map(
    (existingPage?.data ?? []).map((p) => [p.sku, p.id]),
  );

  const reset = (): void => {
    setRows([]);
    setHeaderError(undefined);
    setProgress(0);
    setImporting(false);
    setFileList([]);
  };

  const onUpload = (file: File): boolean => {
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result ?? '');
      const parsed = parseCsv(text);
      setHeaderError(parsed.headerError);
      setRows(parsed.rows);
    };
    reader.readAsText(file);
    return false; // prevent antd auto-upload
  };

  const validRows = rows.filter((r) => r.errors.length === 0);
  const invalidRows = rows.filter((r) => r.errors.length > 0);

  const onImport = async (): Promise<void> => {
    setImporting(true);
    setProgress(0);
    let done = 0;
    let okCount = 0;
    let failCount = 0;
    for (const r of validRows) {
      const payload = {
        name: { it: r.nameIt },
        description: { it: r.descIt },
        price: r.price,
        currency: 'EUR',
        sku: r.sku,
        category: r.category,
        images: r.images,
        isAvailable: r.isAvailable,
        sizes: r.sizes,
      };
      try {
        const existingId = existingBySku.get(r.sku);
        if (existingId) {
          await updateMutation.mutateAsync({ id: existingId, input: payload });
        } else {
          await createMutation.mutateAsync(payload);
        }
        okCount += 1;
      } catch {
        failCount += 1;
      }
      done += 1;
      setProgress(Math.round((done / validRows.length) * 100));
    }
    setImporting(false);
    if (failCount === 0) {
      message.success(`Importati ${okCount} prodotti`);
      reset();
      onClose();
    } else {
      message.warning(`Importati ${okCount}, ${failCount} falliti`);
    }
  };

  return (
    <Modal
      open={open}
      title="Importa prodotti da CSV"
      width={760}
      onCancel={() => {
        if (!importing) {
          reset();
          onClose();
        }
      }}
      footer={
        <Space>
          <Button onClick={() => reset()} disabled={importing}>
            Reset
          </Button>
          <Button
            type="primary"
            disabled={validRows.length === 0 || importing}
            loading={importing}
            onClick={onImport}
          >
            Importa {validRows.length} righe
          </Button>
        </Space>
      }
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Colonne richieste"
        description={
          <Typography.Text code style={{ fontSize: 12 }}>
            sku,nameIt,descIt,price,category,sizes [,images,isAvailable]
            {'\n'}sizes format: S:10|M:25|L:15 — images format: url1|url2
          </Typography.Text>
        }
      />

      {!rows.length && (
        <Upload.Dragger
          accept=".csv,text/csv"
          maxCount={1}
          fileList={fileList}
          beforeUpload={(f) => onUpload(f)}
          onChange={(info) => setFileList(info.fileList.slice(-1))}
          showUploadList={false}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Trascina qui il file CSV o clicca</p>
          <p className="ant-upload-hint">Solo .csv, max 1 file</p>
        </Upload.Dragger>
      )}

      {headerError && <Alert type="error" showIcon message={headerError} />}

      {rows.length > 0 && (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Space>
            <Tag color="green">{validRows.length} valide</Tag>
            {invalidRows.length > 0 && (
              <Tag color="red">{invalidRows.length} con errori</Tag>
            )}
            {existingBySku.size > 0 && (
              <Tag color="blue">
                Aggiornerò {
                  validRows.filter((r) => existingBySku.has(r.sku)).length
                } prodotti esistenti
              </Tag>
            )}
          </Space>

          {importing && <Progress percent={progress} />}

          <Table
            size="small"
            rowKey="rowIndex"
            dataSource={rows.slice(0, 50)}
            pagination={false}
            scroll={{ y: 280 }}
            columns={[
              {
                title: 'Riga',
                dataIndex: 'rowIndex',
                width: 60,
              },
              {
                title: 'SKU',
                dataIndex: 'sku',
                width: 140,
                render: (v: string) => (
                  <Typography.Text code style={{ fontSize: 11 }}>
                    {v}
                  </Typography.Text>
                ),
              },
              { title: 'Nome', dataIndex: 'nameIt', ellipsis: true },
              { title: 'Cat.', dataIndex: 'category', width: 90 },
              {
                title: 'Prezzo',
                dataIndex: 'price',
                width: 80,
                render: (v: number) => `${v.toFixed(2)} €`,
              },
              {
                title: 'Sizes',
                dataIndex: 'sizes',
                render: (v: Array<{ size: string; stock: number }>) =>
                  v.map((s) => `${s.size}:${s.stock}`).join(' '),
              },
              {
                title: 'Errori',
                render: (_v, r: ParsedRow) =>
                  r.errors.length === 0 ? (
                    <Tag color="green">OK</Tag>
                  ) : (
                    <Typography.Text type="danger" style={{ fontSize: 11 }}>
                      {r.errors.join(', ')}
                    </Typography.Text>
                  ),
              },
            ]}
          />
          {rows.length > 50 && (
            <Typography.Text type="secondary">
              Mostro le prime 50 righe di {rows.length}.
            </Typography.Text>
          )}
        </Space>
      )}
    </Modal>
  );
}
